import {
  Boxes,
  Recycle,
  Truck,
  FileCheck2,
  Ruler,
  Layers,
  ClipboardCheck,
} from "lucide-react";
import {
  Nav,
  Footer,
  PageHero,
  CtaBlock,
  TrustStrip,
  RecyclingSubNav,
  usePageSeo,
  CARDBOARD_IMG_URL,
} from "../layout/shared";

const WHAT_WE_TAKE = [
  {
    t: "Cardboard boxes and sheets",
    d: "Cardboard boxes and sheets from farms and businesses, assessed for the appropriate recycling route.",
    icon: Boxes,
  },
  {
    t: "Retail and warehouse volumes",
    d: "Regular volumes from shops, retail units, distribution and packaging operations.",
    icon: Truck,
  },
  {
    t: "Farm and site clearances",
    d: "Cardboard building up around a farm, workshop or recycling yard, cleared in one movement.",
    icon: Layers,
  },
  {
    t: "Post-consumer packaging",
    d: "Cardboard packaging from confirmed sources, matched to a recycling outlet.",
    icon: Recycle,
  },
];

const PREPARE = [
  "Keep cardboard dry and under cover where possible. Wet cardboard loses value and can be rejected.",
  "Flatten boxes and remove polystyrene inserts, plastic wrap, tape residue and non-fibre packaging.",
  "Keep it separate from general waste, food and heavily soiled packaging.",
  "Tell us the storage format: loose, in wheelie bins, palletised, in a cage or already baled.",
];

const HOW_WE_TAKE = [
  { icon: Truck, t: "Scheduled or one-off", d: "Regular collection rounds or single clearances arranged around your volume." },
  { icon: Ruler, t: "Vehicle matched to volume", d: "The right vehicle and access are confirmed before booking. Partner vehicles may be arranged where useful." },
  { icon: Recycle, t: "Prepared for reprocessing", d: "Baled on site or at our yard as appropriate and moved on to an authorised reprocessor." },
  { icon: FileCheck2, t: "Documentation retained", d: "The relevant waste transfer information is provided or retained for the agreed movement." },
];

const FAQS = [
  {
    q: "Do you take mixed paper and cardboard together?",
    a: "Sometimes. Grade rules vary between outlets. Tell us the split and we will confirm before booking.",
  },
  {
    q: "How much cardboard is enough for a collection?",
    a: "It depends on distance, access and current outlet demand. Send photographs and a rough volume and we will tell you whether a scheduled round, a one-off clearance or a booked drop-off is the most economical option.",
  },
  {
    q: "Can you provide bins or a baler on site?",
    a: "On-site equipment is arranged case by case, matched to volume, space and how long you will need it. It is confirmed in writing before delivery.",
  },
];

const Cardboard = () => {
  usePageSeo({
    title: "Business Cardboard Collections · 3 Counties Recycling",
    description:
      "One-off and scheduled cardboard collections for suitable volumes from farms and businesses across Worcestershire, Herefordshire and Gloucestershire.",
    path: "/cardboard-recycling",
  });

  return (
    <main data-testid="cardboard-root" className="bg-white text-[#1a1a1a]">
      <Nav />
      <RecyclingSubNav />
      <PageHero
        kicker="Cardboard · one-off clearances · scheduled collections"
        title="Business cardboard collections across the"
        accent="three counties."
        body="Cardboard takes up valuable space when it is stored loose or mixed with other packaging. We assess one-off clearances and scheduled collections for suitable volumes from businesses, retail units, recycling sites and farms across Worcestershire, Herefordshire and Gloucestershire."
        image={CARDBOARD_IMG_URL}
      />

      <section className="bg-white py-16 border-b border-[#024120]/10">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <TrustStrip variant="light" />
        </div>
      </section>

      {/* What we take */}
      <section data-testid="cardboard-what-we-take" className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="mb-14 max-w-3xl">
            <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
              / What we take
            </p>
            <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
              Cardboard from suitable sources.
            </h2>
            <p className="mt-6 text-[#4A4A4A] text-base md:text-lg leading-relaxed">
              We confirm the volume, presentation, loading arrangements,
              documentation and receiving route before every collection.
              Acceptance depends on grade, contamination and the current
              recycling outlet.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {WHAT_WE_TAKE.map((it) => (
              <div
                key={it.t}
                data-testid={`cardboard-take-${it.t
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")}`}
                className="bg-[#F2F2F2] p-7 border border-[#024120]/5 flex flex-col"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center bg-[#024120] text-[#46B649]">
                  <it.icon className="w-6 h-6" strokeWidth={1.6} />
                </span>
                <p className="mt-6 font-display font-bold text-lg text-[#024120]">
                  {it.t}
                </p>
                <p className="mt-2 text-[15px] text-[#4A4A4A] leading-relaxed">
                  {it.d}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Prepare */}
      <section className="bg-[#F2F2F2] py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / Prepare the load
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
            Four things that keep cardboard recyclable.
          </h2>
          <ul className="mt-10 space-y-4">
            {PREPARE.map((p, i) => (
              <li
                key={p}
                className="bg-white border-l-4 border-[#46B649] px-6 py-5 flex items-start gap-4"
              >
                <span className="font-display font-black text-2xl text-[#FF6600] leading-none tabular-nums">
                  0{i + 1}
                </span>
                <span className="text-[15px] text-[#024120] leading-relaxed">
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* How we take */}
      <section className="bg-white py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / How we take it
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
            Collection matched to your volume and site.
          </h2>
          <div className="mt-12 grid md:grid-cols-2 gap-5">
            {HOW_WE_TAKE.map((h) => (
              <div
                key={h.t}
                className="bg-[#F2F2F2] p-7 border-l-4 border-[#46B649] flex items-start gap-5"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center bg-[#024120] text-[#46B649] shrink-0">
                  <h.icon className="w-6 h-6" strokeWidth={1.6} />
                </span>
                <div>
                  <p className="font-display font-bold text-lg text-[#024120]">
                    {h.t}
                  </p>
                  <p className="mt-2 text-[15px] text-[#4A4A4A] leading-relaxed">
                    {h.d}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-[#F2F2F2] py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / Frequently asked questions
          </p>
          <h2 className="mt-3 font-display font-black text-3xl md:text-4xl lg:text-[44px] tracking-tight text-[#024120] leading-[1.1]">
            What businesses ask before booking cardboard.
          </h2>
          <div className="mt-10 space-y-4">
            {FAQS.map((f, i) => (
              <details
                key={f.q}
                data-testid={`cardboard-faq-${i + 1}`}
                className="bg-white border border-[#024120]/10 group"
              >
                <summary data-testid={`cardboard-faq-toggle-${i + 1}`} className="cursor-pointer list-none px-6 py-5 flex items-start justify-between gap-4 font-display font-bold text-[#024120] text-lg">
                  <span>{f.q}</span>
                  <ClipboardCheck className="w-5 h-5 text-[#46B649] shrink-0 mt-0.5" />
                </summary>
                <div className="px-6 pb-6 text-[15px] text-[#4A4A4A] leading-relaxed">
                  {f.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <CtaBlock
        title="Send us a photo and a rough volume."
        body="We will confirm whether a scheduled round, a one-off clearance or a booked drop-off is the most economical option for your cardboard."
        primaryLabel="Request a cardboard collection"
      />

      <Footer />
    </main>
  );
};

export default Cardboard;
